import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { getInvoicingDocuments, saveReadyForInvoicing } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleReadyForInvoicingDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination, formatDateMoment } from 'utils'
import InvoicingDocumentsDownloadLink from './InvoicingDocumentsDownloadLink'

interface ReadyForInvoicingCardProps {
  refreshInvoicing: (invoicing: InvoicingDto) => void
  readyForInvoicing: VehicleReadyForInvoicingDto[]
}

export default function ReadyForInvoicingCard({ refreshInvoicing, readyForInvoicing }: ReadyForInvoicingCardProps) {
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isSavingAndDownloading, setIsSavingAndDownloading] = useState<boolean>(false)
  const [isSavingAndSendingToQuickBooks, setIsSavingAndSendingToQuickBooks] = useState<boolean>(false)
  const [isSavingAndMarkingInvoiced, setIsSavingAndMarkingInvoiced] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleReadyForInvoicingDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleReadyForInvoicingDto[]>([])

  const [quickBooksInvoiceNumber, setQuickBooksInvoiceNumber] = useState<string>('')

  const onChangeSortByDirection = (propertyName: keyof VehicleReadyForInvoicingDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  useEffect(() => {
    setIsSubmitting(isSaving || isSavingAndDownloading || isSavingAndSendingToQuickBooks || isSavingAndMarkingInvoiced)
  }, [isSaving, isSavingAndDownloading, isSavingAndSendingToQuickBooks, isSavingAndMarkingInvoiced])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm='auto'>
            <Typography fontWeight='bold' variant='h6'>
              Ready For Invoicing
            </Typography>
          </Grid>

          <Grid item xs={12} sm='auto'>
            <TextField
              disabled={isSubmitting || selected.length === 0}
              label='QB Invoice #'
              InputLabelProps={{ shrink: true }}
              onChange={e => setQuickBooksInvoiceNumber(e.target.value)}
              size='small'
              value={quickBooksInvoiceNumber ?? ''}
            />
          </Grid>

          <Grid item xs={12} sm='auto'>
            <Button
              disabled={isSubmitting || selected.length === 0}
              onClick={() => setSelected(selected.map(s => ({ ...s, quickBooksInvoiceNumber })))}
              variant='contained'
            >
              APPLY QB INVOICE # TO SELECTED VEHICLES
            </Button>
          </Grid>

          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableCell padding='checkbox'></TableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='importerStockNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Importer Stock #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='buyerExporterCompany'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Buyer/Exporter
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='papsNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    PAPS #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    VIN
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Year
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Make
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Model
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    disabled
                    keyName='vehicleGuid'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Invoicing Documents
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='readyToInvoiceDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Ready To Invoice
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='sentToQuickBooksDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Sent To QuickBooks
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    disabled
                    keyName='quickBooksInvoiceNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    QB Invoice #
                  </OrderableTableCell>
                </TableHead>

                <TableBody>
                  {readyForInvoicing
                    ?.sort(clientSideOrderBy<VehicleReadyForInvoicingDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                            disabled={isSubmitting}
                            onChange={() => {
                              if (selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)) {
                                setSelected(selected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid))
                              } else {
                                setSelected([...selected, vehicle])
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.buyerExporterCompany}</TableCell>

                        <TableCell>{vehicle.papsNumber}</TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell>
                          <InvoicingDocumentsDownloadLink vehicleGuid={vehicle.vehicleGuid} />
                        </TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.readyToInvoiceDateTime) : '-'}</TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.sentToQuickBooksDateTime) : '-'}</TableCell>

                        <TableCell>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputLabelProps={{ shrink: true }}
                            label='Invoice #'
                            onChange={e =>
                              setSelected(selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, quickBooksInvoiceNumber: e.target.value } : v)))
                            }
                            size='small'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].quickBooksInvoiceNumber
                                : vehicle.quickBooksInvoiceNumber ?? ''
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={readyForInvoicing.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Ready For Invoicing:**

This list displays vehicles that have been marked as ready for invoicing.

**Apply QB Invoice #:**

Overwrites the "QB Invoice #" field for selected vehicles with the value entered in the QB Invoice # field above the table.

**Download Invoicing Documents:**

Press "Download" in the Invoicing Documents column to generate and download a PDF for a specific vehicle.

Select multiple vehicles and press "Save And Download Invoicing Documents For Selected Vehicles" to generate a combined PDF.

The PDF includes 7501, Recall Repair Orders, and uploaded documents (FedEx, Replacement Label, Repairs, Vehicle Clean Up, Miscellaneous).

**Save Button:**

Saves any entered QB Invoice # to its respective vehicle record.

**Save And Send Selected Vehicles To QuickBooks Button:**

Populates the "Sent To QuickBooks" column with the current date and initiates the data transfer to QuickBooks Pro Desktop 2022.

**Save And Mark Selected Vehicles As Invoiced Button:**

Checks that all selected vehicles have a QB Invoice #.

If any QB Invoice # field is blank, the system highlights it as an error.

If all QB Invoice # fields are filled, the selected vehicles are moved to the Invoiced list.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSaving}
                onClick={() => {
                  setIsSaving(true)
                  saveReadyForInvoicing(selected, false, false)
                    .then(res => {
                      enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSaving(false))
                }}
                variant='contained'
              >
                SAVE
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndDownloading}
                onClick={() => {
                  setIsSavingAndDownloading(true)
                  saveReadyForInvoicing(selected, false, false).then(res => {
                    enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                    refreshInvoicing(res.value)
                    setSelected([])
                    setPage(0)
                    getInvoicingDocuments(selected.map(v => v.vehicleGuid)).finally(() => setIsSavingAndDownloading(false))
                  })
                }}
                variant='contained'
              >
                SAVE AND DOWNLOAD INVOICING DOCUMENTS FOR SELECTED VEHICLES
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndSendingToQuickBooks}
                onClick={() => {
                  setIsSavingAndSendingToQuickBooks(true)
                  saveReadyForInvoicing(selected, true, false)
                    .then(res => {
                      enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSavingAndSendingToQuickBooks(false))
                }}
                variant='contained'
              >
                SAVE AND SEND SELECTED VEHICLES TO QUICKBOOKS
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndMarkingInvoiced}
                onClick={() => {
                  setIsSavingAndMarkingInvoiced(true)
                  saveReadyForInvoicing(selected, false, true)
                    .then(res => {
                      enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSavingAndMarkingInvoiced(false))
                }}
                variant='contained'
              >
                SAVE AND MARK SELECTED VEHICLES AS INVOICED
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
